import {
  BlockTitle,
  Button,
  EmptyState,
  Skeleton,
  Surface,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
  titleCase,
} from '@westhetics/ui'
import { useState } from 'react'
import { useLocation, useMatch, useNavigate } from 'react-router'
import { ListFooter } from '@/app/list-footer'
import { LoadError } from '@/app/load-error'
import { routes } from '@/routes'
import { AnamnesisDetail } from './anamnesis-detail'
import { ActionsCell, SentCell, StatusCell } from './anamnesis-row'
import { useAnamneses, usePatientContact } from './api'
import { IssueAnamnesis } from './issue-anamnesis'
import { canOpen } from './labels'
import { PrintBlankFormButton } from './print-blank-form'
import { useAnamnesisActions } from './use-anamnesis-actions'

/**
 * A aba Anamnese da ficha da paciente (docs/areas/anamnese.md, "Lado B · A clínica lê"; lista em
 * docs/api/anamneses.md, `GET /anamneses?patientId=`). A ficha (`features/patients`) só a encaixa:
 * tudo o que é de anamnese mora nesta pasta.
 *
 * "Imprimir ficha em branco" baixa o questionário vigente com nome e CPF dela, para preencher à mão
 * (RN-ANA-24).
 *
 * A aba lista as anamneses da paciente e nada mais, emite uma nova e abre a anamnese "logo abaixo,
 * na mesma tela": em `/patients/:id/anamneses/:anamnesisId` a lista dá lugar à leitura (respondida)
 * ou ao registro do envio (não preenchida), com "← Voltar à lista".
 */

/**
 * A âncora que abre a aba já com o envio à vista: o "Enviar anamnese" do cabeçalho da ficha aponta
 * para `routes.patientTab(id, 'anamneses') + '#' + SEND_ANAMNESIS_ANCHOR`.
 */
export const SEND_ANAMNESIS_ANCHOR = 'send'

export function PatientAnamnesesTab({ patientId }: { patientId: string }) {
  const navigate = useNavigate()
  const match = useMatch(routes.patientAnamnesis(':patientId', ':anamnesisId'))
  const openId = match?.params.patientId === patientId ? match.params.anamnesisId : undefined

  if (openId) {
    return (
      <AnamnesisDetail
        key={openId}
        anamnesisId={openId}
        patientId={patientId}
        onBack={() => navigate(routes.patientTab(patientId, 'anamneses'))}
      />
    )
  }
  return <PatientAnamnesesList key={patientId} patientId={patientId} />
}

function PatientAnamnesesList({ patientId }: { patientId: string }) {
  const navigate = useNavigate()
  const location = useLocation()
  const [sending, setSending] = useState(location.hash === `#${SEND_ANAMNESIS_ANCHOR}`)
  const [page, setPage] = useState(1)
  const query = useAnamneses({ filter: 'all', patientId, page })
  // O contato só é pedido quando o envio abre: diz quais canais a paciente pode receber.
  const contact = usePatientContact(sending ? patientId : null)
  const actions = useAnamnesisActions()

  const anamneses = query.data?.data ?? []
  const total = query.data?.meta.total ?? 0
  // Sem o cadastro à mão, o nome vem das anamneses que ela já tem; os canais, a API confere.
  const knownName = contact.data?.name ?? anamneses[0]?.patient.name ?? null
  const busy = actions.isResending || actions.isCanceling

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="eyebrow">Anamneses desta paciente{query.data ? ` · ${total}` : ''}</h2>
        <div className="flex flex-wrap items-center gap-2">
          {/* No papel, com nome e CPF dela já impressos (RN-ANA-24). */}
          <PrintBlankFormButton patientId={patientId} />
          {!sending && anamneses.length > 0 ? (
            <Button variant="outline" size="sm" onClick={() => setSending(true)}>
              Enviar anamnese
            </Button>
          ) : null}
        </div>
      </div>

      {sending ? (
        <Surface className="max-desk:px-4">
          <BlockTitle
            title={knownName ? `Enviar anamnese para ${titleCase(knownName)}` : 'Enviar anamnese'}
            note="São 5 blocos curtos, cerca de 4 minutos, no celular dela, sem cadastro nem senha. O link vale por 7 dias."
          />
          <div className="mt-4">
            {contact.isPending ? (
              <div role="status" aria-label="Carregando o contato da paciente">
                <Skeleton className="h-[44px] w-full" />
                <Skeleton className="mt-3 h-[96px] w-full" />
              </div>
            ) : knownName ? (
              <IssueAnamnesis
                patient={contact.data ?? { id: patientId, name: knownName }}
                onClose={() => setSending(false)}
              />
            ) : (
              <LoadError
                title="Não conseguimos carregar o cadastro da paciente"
                error={contact.error}
                onRetry={() => void contact.refetch()}
              />
            )}
          </div>
        </Surface>
      ) : null}

      {actions.notice}

      {query.isError ? (
        <LoadError
          title="Não conseguimos carregar as anamneses desta paciente"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : query.isPending ? (
        <TableSkeleton rows={3} columns={4} />
      ) : anamneses.length === 0 ? (
        <EmptyState
          title="Nenhuma anamnese desta paciente ainda"
          description="A anamnese é o que a profissional lê antes de aplicar: medicamentos, alergias e a expectativa de resultado, com data, hora e hash do envio."
          action={
            sending ? undefined : (
              <Button variant="outline" onClick={() => setSending(true)}>
                Enviar anamnese
              </Button>
            )
          }
        />
      ) : (
        <div>
          <Table>
            <TableHeader>
              <tr>
                <TableHead>Envio</TableHead>
                <TableHead>Procedimento na época</TableHead>
                <TableHead>Profissional</TableHead>
                <TableHead>Status</TableHead>
                <TableHead aria-label="Ações" />
              </tr>
            </TableHeader>
            <TableBody>
              {anamneses.map((anamnesis) => (
                <TableRow
                  key={anamnesis.id}
                  critical={anamnesis.status === 'expired'}
                  onClick={
                    canOpen(anamnesis)
                      ? () => navigate(routes.patientAnamnesis(patientId, anamnesis.id))
                      : undefined
                  }
                >
                  <TableCell label="Envio" muted>
                    <SentCell anamnesis={anamnesis} />
                  </TableCell>
                  <TableCell label="Procedimento">
                    {anamnesis.procedureAtSendTime?.name ?? 'Sem procedimento'}
                  </TableCell>
                  <TableCell label="Profissional" muted>
                    {anamnesis.practitioner
                      ? titleCase(anamnesis.practitioner.name)
                      : 'Sem profissional'}
                  </TableCell>
                  <TableCell label="Status">
                    <StatusCell anamnesis={anamnesis} />
                  </TableCell>
                  <TableCell>
                    <ActionsCell
                      anamnesis={anamnesis}
                      busy={busy}
                      onResend={(target) => actions.resend(target)}
                      onCancel={(target) => actions.askCancel(target)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {query.data ? (
            <ListFooter
              meta={query.data.meta}
              noun={['anamnese', 'anamneses']}
              note="clique numa linha para abrir a ficha de anamnese"
              onPage={setPage}
            />
          ) : null}
        </div>
      )}

      {actions.dialog}
    </div>
  )
}
